import Vue from 'vue';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import Project from 'src/api/project';
import { CLEAR_REQUEST_ERRORS } from 'src/store/types';
import FormatUtils from 'src/utils/formatters';
import Modal from 'src/components/widgets/Modal.vue';
import RadioButtons from "components/widgets/RadioButtons.vue";
import ToggleSliderInput from 'src/components/forms/ToggleSliderInput.vue';
import { BfTextInput, BfDivider, BfButton } from 'components/Butterfly';
import { Form, Field, defineRule } from 'components/forms';
import IntegrationButton from './IntegrationButton.vue';
import 'components/forms/rules/required';
import 'components/forms/rules/alphanumeric';
defineRule('bucketName', (value) => {
    if (typeof value !== 'string') {
        return false;
    }
    // See https://docs.aws.amazon.com/AmazonS3/latest/dev/BucketRestrictions.html#bucketnamingrules
    const invalidChars = value.replace(/[a-z0-9-.]+/g, '');
    if (!invalidChars) {
        return true;
    }
    const uniqInvalidCharsWithoutSpace = _.uniq(invalidChars.replace(' ', '').split('')).join(' ');
    const hadSpaces = invalidChars.includes(' ');
    const andOthers = hadSpaces && invalidChars.length > 1;
    return `This bucket name does not match naming rules. These characters are not allowed: ${hadSpaces ? 'spaces' : ''}${andOthers ? ' and ' : ''}${uniqInvalidCharsWithoutSpace}`;
});
export default Vue.extend({
    // required for old vee-validate2
    $_veeValidate: { validator: 'new' },
    components: { Modal, ToggleSliderInput, BfDivider, BfTextInput, Form, Field, BfButton, RadioButtons, IntegrationButton },
    beforeRouteEnter(to, from, next) {
        Project.getAllIntegrations()
            .then((response) => {
            // pack the list into a dict indexed by type
            next((component) => {
                component.integrationsDict = {};
                for (let i of response.results) {
                    component.integrationsDict[i.type] = i;
                }
            });
        })
            .catch(() => {
            next();
        });
    },
    data() {
        return {
            initialFormValues: {
                qualtrics: {
                    qualtricsToken: '',
                    qualtricsClientId: '',
                    qualtricsClientSecret: '',
                    qualtricsDcid: '',
                    qualtricsOrgid: '',
                    qualtricsmtlsEnable: false,
                    qualtricsEnabled: true,
                },
                s3: {
                    s3Bucket: '',
                    s3Enabled: true,
                },
                gcs: {
                    gcsBucket: '',
                    gcsEnabled: true,
                },
                delighted: {
                    delightedToken: '',
                    delightedEnabled: true,
                },
            },
            isLoading: false,
            // Each integration's type as a string. They match the enum values
            // defined in the backend.
            delighted: 'delighted',
            qualtrics: 'qualtrics',
            s3: 's3',
            gcs: 'gcs',
            surveyMonkey: 'survey_monkey',
            tiers: {
                [this.s3]: 'standard',
                [this.delighted]: 'standard',
                [this.surveyMonkey]: 'standard',
                [this.qualtrics]: 'premium',
            },
            displayName: {
                [this.s3]: 'standard',
                [this.gcs]: 'standard',
                [this.delighted]: 'standard',
                [this.surveyMonkey]: 'standard',
                [this.qualtrics]: 'premium',
            },
            providerModal: undefined,
            // integrationsDict maps the integration type to an object for each
            // integration. An example of the object:
            // {
            //    type: 'delighted',
            //    site: 4,
            //    enabled: true,
            //    details: {
            //      apiToken: 'jgfo309frnsdjsoFJKLDSieowkl'
            //    }
            // Note: if the integration has not been configured on the site, it is
            // missing from this object.
            integrationsDict: {},
            serverError: '',
            selectedAuthValue: "apiToken",
            authOptions: [
                { value: "apiToken", label: "API Token" },
                { value: "oauth", label: "OAuth" },
            ],
        };
    },
    metaInfo() {
        return {
            title: this.currentSite ? `${this.currentSite.site_name} Integrations - Kapiche` : null
        };
    },
    computed: {
        ...mapGetters([
            'featureFlags',
            'currentSite',
        ]),
        surveyMonkeyData() {
            return this.integrationsDict && this.integrationsDict[this.surveyMonkey];
        },
        surveyMonkeyOAuthLink() {
            // todo: this should really be an environment variable
            const env = process.env.APP_ENV;
            const hostMap = {
                dev: { redirect_base: 'http%3A%2F%2Flocalhost%3A8080', client_id: 'TwTxRZa6RnKQT8ToB-lGOA' },
                staging: { redirect_base: 'https%3A%2F%2Fapp.kapiche-dev.com', client_id: '5OEvqnUXSQuIP9f336bATA' },
                production: { redirect_base: 'https%3A%2F%2Fapp.kapiche.com', client_id: 'MX0XLeACTPutD-yaOxdL2g' }, // nosec
            };
            // https://api.surveymonkey.net/v3/docs?api_key=3yr7n6m8sjwvm48x8nhxej52#oauth-2-0-flow
            const base = 'https://api.surveymonkey.com/oauth/authorize?response_type=code';
            const redirect_uri = `${hostMap[env].redirect_base}%2Fsurvey-monkey-config%2F`;
            const client_id = hostMap[env].client_id;
            // https://auth0.com/docs/protocols/oauth2/redirect-users
            const state = `${this.currentSite.domain}`;
            return `${base}&redirect_uri=${redirect_uri}&client_id=${client_id}&state=${state}`;
        }
    },
    methods: {
        startCase: FormatUtils.startCase,
        clearErrorMessage() {
            this.serverError = '';
        },
        updateIntegration(type) {
            return async (values) => {
                const endpoint = this.integrationsDict.hasOwnProperty(type) === false ? Project.createNewIntegration : Project.updateIntegration;
                let payload = { type };
                if (type === this.qualtrics) {
                    if (this.selectedAuthValue === 'apiToken') {
                        Object.assign(payload, {
                            'details': {
                                'datacenterId': values.qualtricsDcid,
                                'organizationId': values.qualtricsOrgid,
                                'apiToken': values.qualtricsToken,
                                'mTLS': values.qualtricsmtlsEnable,
                            },
                            'enabled': values.qualtricsEnabled // defaults to true on initial upload
                        });
                    }
                    else if (this.selectedAuthValue === 'oauth') {
                        Object.assign(payload, {
                            'details': {
                                'datacenterId': values.qualtricsDcid,
                                'organizationId': values.qualtricsOrgid,
                                'clientId': values.qualtricsClientId,
                                'clientSecret': values.qualtricsClientSecret,
                                'mTLS': values.qualtricsmtlsEnable,
                            },
                            'enabled': values.qualtricsEnabled // defaults to true on initial upload
                        });
                    }
                }
                else if (type === this.delighted) {
                    Object.assign(payload, {
                        'details': { 'apiToken': values.delightedToken },
                        'enabled': values.delightedEnabled // defaults to true on initial upload
                    });
                }
                else if (type === this.s3) {
                    Object.assign(payload, {
                        'details': { 'bucket': values.s3Bucket },
                        'enabled': values.s3Enabled // defaults to true on initial upload
                    });
                }
                else if (type === this.gcs) {
                    Object.assign(payload, {
                        'details': { 'bucket': values.gcsBucket },
                        'enabled': values.gcsEnabled // defaults to true on initial upload
                    });
                }
                try {
                    this.isLoading = true;
                    const result = await endpoint(payload);
                    this.integrationsDict[result.type] = result;
                    this.$analytics.track.integration.update(this.providerModal, this.numProjects);
                    this.$refs[`${type}UpdateModal`].hide();
                }
                catch (resp) {
                    if (resp.status === 400) {
                        this.$store.dispatch(CLEAR_REQUEST_ERRORS); // custom error handling
                        let errors = await resp.json();
                        if (errors.hasOwnProperty('non_field_errors')) {
                            this.serverError = errors['non_field_errors'][0];
                        }
                        else {
                            const key = Object.keys(errors)[0];
                            this.serverError = `${key}: ${errors[key][0]}`;
                        }
                    }
                }
                finally {
                    this.isLoading = false;
                }
            };
        },
        removeIntegration(provider) {
            this.isLoading = true;
            Project.deleteIntegration(provider)
                .then((response) => {
                this.isLoading = false;
                this.$refs.deleteModal.hide();
                this.$analytics.track.integration.remove(provider, this.numProjects);
                this.getIntegrations();
            })
                .catch(() => {
                this.isLoading = false;
            });
        },
        popModal(integration) {
            if (integration in this.integrationsDict) {
                Project.countIntegrationProjects(integration)
                    .then((response) => {
                    this.numProjects = response.body;
                });
                this.providerModal = integration;
            }
            // Set the default value for the input field for the modal
            if (integration in this.integrationsDict) {
                if (integration === this.qualtrics) {
                    const current = this.integrationsDict[this.qualtrics];
                    this.$refs.qualtricsForm?.resetForm({
                        qualtricsToken: current.details?.apiToken ?? '',
                        qualtricsClientId: current.details?.clientId ?? '',
                        qualtricsClientSecret: current.details?.clientSecret ?? '',
                        qualtricsDcid: current.details?.datacenterId ?? '',
                        qualtricsOrgid: current.details?.organizationId ?? '',
                        qualtricsmtlsEnable: current.details?.mTLS ?? '',
                        qualtricsEnabled: current.enabled ?? '',
                    });
                }
                else if (integration === this.delighted) {
                    const current = this.integrationsDict[this.delighted];
                    this.$refs.delightedForm?.resetForm({
                        delightedToken: current.details?.apiToken ?? '',
                        delightedEnabled: current.enabled ?? '',
                    });
                }
                else if (integration === this.s3) {
                    const current = this.integrationsDict[this.s3];
                    this.$refs.s3Form?.resetForm({
                        s3Bucket: current.details?.bucket ?? '',
                        s3Enabled: current.enabled ?? '',
                    });
                }
                else if (integration === this.gcs) {
                    const current = this.integrationsDict[this.gcs];
                    this.$refs.gcsForm?.resetForm({
                        gcsBucket: current.details?.bucket ?? '',
                        gcsEnabled: current.enabled ?? '',
                    });
                }
            }
            this.$refs[`${integration}UpdateModal`].show();
        },
        popDeleteModal(integration) {
            this.providerModal = integration;
            this.$refs.deleteModal.show();
        },
        hideModal(ref) {
            ref.hide();
            ref.$children[0]?.resetForm();
        },
        getIntegrations() {
            return Project.getAllIntegrations()
                .then((response) => {
                // pack the list into a dict indexed by type
                this.integrationsDict = {};
                for (let i of response.results) {
                    this.integrationsDict[i.type] = i;
                }
            })
                .catch(() => {
                this.qualtrics = false; // If we get a server error, we should set the qualtrics integration to false
            });
        },
        popSurveyMonkey() {
            window.open(`${this.surveyMonkeyOAuthLink}`, 'popup', 'width=1000,height=1200,');
        },
        changeAuthOption(value) {
            this.selectedAuthValue = value;
        }
    },
});
